/* LEGEND — LADDER "when-machine": floating card + embedded chart-top ribbon.
   Ported from legend/ladder.jsx (receipts-first Rev 2, LADDER_UI_RECEIPTS.md).
   The prototype ran a SIMULATED order-flow story; this reads the REAL server ladder:
   the LadderEngine per-price volume profile + venue-aware reload/iceberg + the
   sequence state machine, hydrated via getLadder and streamed over ladder:{SYM}.

   Receipts-first elements and their wire status (doc §G):
     E2 dollars + ruler   → metrics.soldDollars, frame dvps/dvpsSeeded   (live)
     E3 verdict clock     → badge `since` + client TTL const             (approx)
     E4 badge aging       → states[].since                               (live)
     E5 session tally     → REST signals log + frame `gated`             (live)
     E1 gate checklist    → frame `gates` export (§F) — renders the moment
     E6 rotation dials    → the engine ships it; hidden until then
   Trust chips carry the measured next-1-min direction hit % from the
   488-symbol-day corpus (doc §C) so nobody has to memorize them. */
import React from 'react';
import { Icon } from './Widgets';
import { getLadder, subscribeLadder, subscribeTrades, getVision, subscribeVision } from '../data/source';
import { MODEL_FAMS } from '../lib/vision-families';
import { useVisionLane } from '../lib/use-vision-lane';

const { useState: useStateL, useEffect: useEffectL, useRef: useRefL } = React;
const hl = React.createElement;

/* ---------- icons (simple glyphs, Legend line style) ---------- */
const svg = (kids: any, extra?: any) => hl('svg', Object.assign({ viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' }, extra || {}), kids);
const LIcon: any = {
  ladder: () => svg([hl('path', { key: 'a', d: 'M7 3v18M17 3v18' }), hl('path', { key: 'b', d: 'M7 7h10M7 12h10M7 17h10', strokeWidth: 1.6 })]),
  eye: () => svg([hl('path', { key: 'a', d: 'M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z' }), hl('circle', { key: 'b', cx: 12, cy: 12, r: 2.6 })]),
  catch: () => svg([hl('path', { key: 'a', d: 'M12 3v9' }), hl('path', { key: 'b', d: 'M8 8l4 4 4-4' }), hl('path', { key: 'c', d: 'M4 16c0 3 3 5 8 5s8-2 8-5' })]),
  fade: () => svg([hl('path', { key: 'a', d: 'M4 6h16', opacity: .95 }), hl('path', { key: 'b', d: 'M6 12h12', opacity: .6 }), hl('path', { key: 'c', d: 'M9 18h6', opacity: .3 })]),
  bolt: () => svg([hl('path', { key: 'a', d: 'M13 2L4 14h7l-1 8 9-12h-7z', fill: 'currentColor', stroke: 'none' })]),
  breakout: () => svg([hl('path', { key: 'a', d: 'M4 15l6-6 4 4 6-7' }), hl('path', { key: 'b', d: 'M15 6h5v5' })]),
  ban: () => svg([hl('circle', { key: 'a', cx: 12, cy: 12, r: 9 }), hl('path', { key: 'b', d: 'M6 6l12 12' })]),
  reclaim: () => svg([hl('path', { key: 'a', d: 'M3 8l5 8 4-6 3 3 6-9' })]),
  thin: () => svg([hl('path', { key: 'a', d: 'M2 12h5l2-4 3 8 2-4h8', strokeWidth: 1.7 })]),
  boltx: () => svg([hl('path', { key: 'a', d: 'M13 2L6 12h5l-1 7', strokeWidth: 1.8 }), hl('path', { key: 'b', d: 'M14 14l6 6M20 14l-6 6', strokeWidth: 1.8 })]),
  stairs: () => svg([hl('path', { key: 'a', d: 'M3 6h5v4h5v4h5v4h3', strokeWidth: 1.8 }), hl('path', { key: 'b', d: 'M15 20l3 1 1-3', strokeWidth: 1.6 })]),
  turn: () => svg([hl('path', { key: 'a', d: 'M6 20V10a6 6 0 0 1 12 0v4', strokeWidth: 1.9 }), hl('path', { key: 'b', d: 'M14 11l4 4 4-4', strokeWidth: 1.9 })]),
  check: () => svg([hl('path', { key: 'a', d: 'M5 12l4 4 10-10', strokeWidth: 2.8 })]),
  cross: () => svg([hl('path', { key: 'a', d: 'M6 6l12 12M18 6L6 18', strokeWidth: 2.4 })]),
};

/* ---------- state + sequence metadata (UI vocab) ---------- */
// Outcome-backtest semantics (3 months, every traded symbol-day, 04:00–20:00):
// ABSORPTION/EXHAUSTION are SUPPLY WARNINGS — heavy selling being "absorbed"
// at a level during a pullback broke the level within 15min in 76-82% of
// episodes (vs 65% base). The dip-buy signal is TURN — the rotation (sellers
// collapse, buyers surge, price holds): level held 39-45% vs 27% base.
// trust = next-1-min direction hit % on the 488-symbol-day corpus (base ≈44%);
// kind: verdict (the product) / pending (armed, awaiting verdict) / narration.
const L_STATES: any = {
  watching:     { label: 'Watching',      tone: 'idle',   icon: 'eye',      tier: 'idle' },
  absorption:   { label: 'Heavy Supply',  tone: 'down',   icon: 'catch',    tier: 'quiet', trust: 44, kind: 'narration', veto: true },
  exhaustion:   { label: 'Supply Pause',  tone: 'down',   icon: 'fade',     tier: 'quiet', trust: 40, kind: 'narration', veto: true },
  turn:         { label: 'Turn — Dip Bought', tone: 'up', icon: 'turn',     tier: 'loud',  trust: 47, kind: 'verdict' },
  flip:         { label: 'Flip',          tone: 'up',     icon: 'bolt',     tier: 'quiet', trust: 46, kind: 'pending' },
  breakconfirm: { label: 'Break-Confirm', tone: 'up',     icon: 'breakout', tier: 'loud',  trust: 68, kind: 'verdict' },
  feeding:      { label: 'Feeding',       tone: 'down',   icon: 'ban',      tier: 'quiet', veto: true },
  stoprun:      { label: 'Stop-Run',      tone: 'warn',   icon: 'reclaim',  tier: 'loud',  trust: 84, kind: 'verdict' },
  flipfail:     { label: 'Flip Failed',   tone: 'down',   icon: 'boltx',    tier: 'loud',  trust: 73, kind: 'verdict' },
  retreat:      { label: 'Retreat',       tone: 'down',   icon: 'stairs',   tier: 'quiet', trust: 49, kind: 'narration' },
};
const L_SEQ: any = {
  supply:   { name: 'Supply at level',  stages: ['Heavy supply', 'Sellers pause', 'Break or turn'] },
  turn:     { name: 'Dip buy',          stages: ['Pullback to level', 'Rotation', 'Hold'] },
  flip:     { name: 'Long flip setup',  stages: ['Absorption', 'Exhaustion', 'Flip'] },
  breakout: { name: 'Breakout',         stages: ['Lid test', 'Consume', 'Confirm'] },
  stoprun:  { name: 'Stop-run',         stages: ['Flush', 'Reclaim', 'Confirm'] },
};
const toneVar = (t: string) => ({ up: 'var(--up)', down: 'var(--down)', warn: 'var(--warn)', accent: 'var(--accent)', idle: 'var(--t3)' } as any)[t] || 'var(--t3)';

// server LadderState enum → UI key; plus the sequence + stage each state sits in.
const STATE_MAP: any = { ABSORPTION: 'absorption', EXHAUSTION: 'exhaustion', TURN: 'turn', FLIP: 'flip', FEEDING: 'feeding', BREAK_CONFIRM: 'breakconfirm', STOP_RUN: 'stoprun', FLIP_FAIL: 'flipfail', RETREAT: 'retreat' };
const SEQ_OF: any = { absorption: 'supply', exhaustion: 'supply', turn: 'turn', flip: 'flip', breakconfirm: 'breakout', feeding: 'breakout', stoprun: 'stoprun', flipfail: 'flip', retreat: 'supply', watching: 'flip' };
const STAGE_OF: any = { absorption: 0, exhaustion: 1, turn: 1, flip: 2, breakconfirm: 2, feeding: 2, stoprun: 1, flipfail: 2, retreat: -1, watching: -1 };
const FLASHY = new Set(['turn', 'flip', 'breakconfirm', 'feeding', 'stoprun', 'flipfail', 'retreat']);
// row state-badge code (E4 — badge paint is history, aged not erased)
const LCODE: any = { absorption: 'ABS', exhaustion: 'EXH', flip: 'FLIP', flipfail: 'FF', breakconfirm: 'BC', turn: 'TURN', retreat: 'RET', stoprun: 'SR', feeding: 'ICE' };

// Buy-score band → prose. The wire carries only { val, band } — the score itself
// is computed service-side (src/apps/screener/buy-score.ts) so the ladder gauge
// and the chart sub-pane can never drift. Label + delta are display-only.
const BS_LABEL: any = { strong: 'Strong buying', buy: 'Buyers ahead', balanced: 'Balanced', sell: 'Sellers ahead', heavy: 'Heavy selling' };

// Classify a raw server LadderState for alarm purposes: null = not loud (badge
// only), else which tone/colour it carries. Shared with the watchlist so a
// ladder signal illuminates the side list even when the card isn't mounted.
// 'feeding' groups the bearish vetoes (feeding/flip-fail/retreat); everything
// else loud is a bullish 'flip'. Single source of truth for the hook & Terminal.
export function ladderSignalKind(serverState: string): 'flip' | 'feeding' | null {
  const key = STATE_MAP[serverState];
  if (!key || !FLASHY.has(key)) return null;
  return (key === 'feeding' || key === 'flipfail' || key === 'retreat') ? 'feeding' : 'flip';
}

const NROWS = 15, MID = 7;
const ICE_BADGE_MIN = 8;        // iceberg ratio threshold — matches server LadderParams.ICE_RATIO_MIN
const DVPS_FLOOR = 250;         // matches server LadderParams.DVPS_FLOOR — at the clamp the "norm"
                                // is a dead-tape session median (premarket gappers), not a ruler
const BADGE_STALE_MS = 45_000;  // E4: dim a row badge past its verdict window — history, not a live arm
// Model hold-call badge (Stage 1 of the model-vs-FSM study, 2026-09-03):
// families where the model's P(hold) is acted on; wall/retained/session/flip
// and break-direction calls graded at-or-below base and are never badged.
// MV-4 (2026-09-17): the list is the contract's families minus the hidden
// set — MODEL_FAMS from lib/vision-families.ts (imported above) — so
// `structural` (the headline family) shows.
const MODEL_P_MIN = 0.5;          // hold chips: level expected to HOLD (toned by side)
const MODEL_WEAK_MAX = 0.35;      // paper chips: level expected to GIVE WAY — shown as
                                  // level STRENGTH (calibrated: 41% held vs 52% base),
                                  // never as a direction call (break-trading graded ~0)
const MODEL_STALE_MS = 150_000;   // vision frames land ~once per 1m bar close;
                                  // 150s tolerates ONE skipped bar (thin tape)
                                  // before declaring the feed dead
const FLIP_ARM_TTL_S = 90;      // E3 approx: client TTL until the §F `flip` arm export lands
const lFmtSz = (v: number) => v >= 1000 ? (v / 1000).toFixed(1) + 'k' : '' + Math.round(v);
const lUsd = (v: number) => v >= 1000 ? '$' + (v / 1000).toFixed(v >= 100000 ? 0 : 1) + 'k' : '$' + Math.round(v);
const lDps = (v: number) => v >= 1000 ? '$' + (v / 1000).toFixed(1) + 'k/s' : '$' + Math.round(v) + '/s';
const decimalsForTick = (tick: number) => tick < 0.01 ? 3 : tick < 0.1 ? 2 : 1;
const clamp = (n: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, n));

/* ---------- receipt composition (from the server's per-state trigger metrics) ---------- */
// E2: dollars-first — every number gets a visible denominator (the dvps ruler).
function buildReceipt(key: string, m: any, pxStr: string, centerStr: string, ctx: any): string {
  switch (key) {
    case 'absorption': {
      // Shares-first (2026-07-29 audit): the ladder rows are shares and these
      // names are read in shares — dollars stay as the secondary cross-symbol
      // anchor. soldDollars flows on the 32fbda2 engine; derive what's missing.
      const usd = m.soldDollars != null ? m.soldDollars : (m.soldInto != null && ctx.trigPx != null ? m.soldInto * ctx.trigPx : null);
      const sh = m.soldInto != null ? m.soldInto : (usd != null && ctx.trigPx ? usd / ctx.trigPx : null);
      // ×norm only while the dvps ruler is real — at the clamp floor the session
      // median is dead premarket tape and the multiple misleads (PCSA read 87×).
      const norm = usd != null && ctx.dvps && ctx.dvps > DVPS_FLOOR ? (usd / (ctx.dvps * 120)).toFixed(1) + '×' : null;
      const lead = sh != null
        ? `<b>${lFmtSz(sh)} sh</b>${usd != null ? ` (${lUsd(usd)})` : ''} sold into ${pxStr}`
        : usd != null ? `<b>${lUsd(usd)}</b> sold into ${pxStr}` : `Heavy supply into ${pxStr}`;
      return `${lead}${norm ? ` — <b>${norm}</b> this stock's 2-min norm` : ' — heavy supply at the level'}${m.reloads ? ` · bid reloaded <b>×${m.reloads}</b>` : ''}`;
    }
    case 'exhaustion':   return `Sell prints <b>${m.sellDropPct != null ? m.sellDropPct : '—'}%</b> vs prior 25 — a pause in supply, <b>not a bottom</b> (sellers usually return)`;
    case 'turn':         return `Sellers <b>−${m.sellTrend != null ? Math.round((1 - m.sellTrend) * 100) : '—'}%</b> vs prior 5m · buyers <b>×${m.buyTrend != null ? m.buyTrend : '—'}</b> · level holding — <b>dip is being bought</b>`;
    case 'flip':         return `Reclaim attempt at ${pxStr} — <b>${m.lifts || 0} ask-lifts</b> &gt; ${m.liftMult != null ? m.liftMult : '—'}× median · <b>verdict pending</b>`;
    case 'breakconfirm': return `Lid ${pxStr} consumed · asks <b>+${m.breakTicks || 0} ticks</b> · pace <b>${m.pace != null ? m.pace : '—'}×</b>${m.dvpsX != null ? ` · <b>${Math.round(m.dvpsX)}×</b> norm` : ''}`;
    case 'feeding':      return `<b>ICE ${m.ice != null ? m.ice : '—'}×</b> on ${pxStr} · pinned <b>${m.pinnedSec || 0}s</b> · pace elevated — hidden seller`;
    case 'stoprun':      return `Wick <b>${pxStr}</b>, full reclaim in <b>${m.reclaimPrints || 0} prints</b> · reclaim vol &gt; flush`;
    case 'flipfail':     return `Round lot printed${m.belowTicks ? ` <b>${m.belowTicks} ticks</b>` : ''} back under ${pxStr}${m.sinceSec != null ? ` <b>${m.sinceSec}s</b> after the flip` : ''} — the reclaim died. <b>The fail is the signal.</b>`;
    case 'retreat':      return `Buyers giving ground — absorption stepping <b>${m.fromPx != null ? m.fromPx : '—'} → ${m.toPx != null ? m.toPx : '—'}</b> (<b>${m.steps || 0} levels</b>)`;
    default:             return `Coiling near <b>${centerStr}</b> — tape balanced, no aggressor edge yet`;
  }
}

/* ---------- audio blip (optional; gated by the `sound` prop) ---------- */
let _lac: any = null;
function blip(kind: string) {
  try {
    _lac = _lac || new ((window as any).AudioContext || (window as any).webkitAudioContext)();
    const ac = _lac; if (ac.state === 'suspended') ac.resume();
    const now = ac.currentTime;
    const mk = (f: number, t0: number, dur: number, type: string, gain: number) => {
      const o = ac.createOscillator(), g = ac.createGain();
      o.type = type || 'sine'; o.frequency.setValueAtTime(f, now + t0);
      g.gain.setValueAtTime(0, now + t0); g.gain.linearRampToValueAtTime(gain || 0.08, now + t0 + 0.012);
      g.gain.exponentialRampToValueAtTime(0.0001, now + t0 + dur);
      o.connect(g); g.connect(ac.destination); o.start(now + t0); o.stop(now + t0 + dur + 0.02);
    };
    if (kind === 'flip') { mk(660, 0, 0.14, 'sine', 0.09); mk(990, 0.09, 0.18, 'sine', 0.08); }
    else { mk(220, 0, 0.22, 'square', 0.05); mk(180, 0.12, 0.26, 'square', 0.05); }
  } catch (e) { /* no-op */ }
}

/* ============================================================
   LIVE FEED — reduce ladder frames + tape into the display snapshot `s`
   ============================================================ */
type Eng = any;
function freshEng(): Eng {
  return {
    hasData: false, tick: 0.01, prec: 2, mid: 0, bid: 0, ask: 0, spread: 0,
    pace: 0, rvol: 0, imbalance: 0.5, thin: true,
    dvps: null, dvpsSeeded: false, gated: false, gates: null,
    buyScore: null, bsPrev: null,
    rows: new Map(), states: [], signal: null, vision: null, flashId: 0, flashKind: 'flip',
    // interpretation layer driving the display: 'model' (default — the study's
    // verdict) or 'fsm' (legacy view, kept as the fallback toggle).
    uiMode: 'model', mvStrongKeys: new Set(),
    tape: [], tapeId: 1, lastPx: 0, spreadHist: [],
    // E5 session tally: loud-signal counts (seeded from the REST log) + gate% frames
    tally: { ff: 0, bc: 0, sr: 0, turn: 0, frames: 0, open: 0 },
  };
}

// E5: count a structural signal into the session tally.
function tallySignal(e: Eng, state: string, cls?: string) {
  if (cls === 'tactical') return;
  if (state === 'FLIP_FAIL') e.tally.ff++;
  else if (state === 'BREAK_CONFIRM') e.tally.bc++;
  else if (state === 'STOP_RUN') e.tally.sr++;
  else if (state === 'TURN') e.tally.turn++;
}

// Apply a WS frame (type:'ladder' snapshot | 'ladder-update' delta).
function applyFrame(e: Eng, f: any) {
  e.hasData = true;
  e.tick = f.tick || e.tick; e.prec = decimalsForTick(e.tick);
  e.mid = f.mid; e.bid = f.bid; e.ask = f.ask; e.spread = f.spread;
  e.pace = f.pace; e.rvol = f.rvol; e.imbalance = f.imbalance; e.thin = f.thin;
  if (f.dvps != null) { e.dvps = f.dvps; e.dvpsSeeded = !!f.dvpsSeeded; }
  // Guarded: an older server build that omits buyScore must not blank the gauge
  // on every delta. Keep the prior value so the gauge can show its own delta.
  if (f.buyScore) { e.bsPrev = e.buyScore ? e.buyScore.val : null; e.buyScore = f.buyScore; }
  e.gated = !!f.gated;
  if (f.gates) e.gates = f.gates; // §F export — absent today, lights up E1/E6 when shipped
  if (f.type === 'ladder') e.rows = new Map(); // full snapshot → rebuild
  for (const r of f.rows || []) e.rows.set(r.idx, { buy: r.buy, sell: r.sell, reloads: r.reloads, ice: r.ice });
  e.states = f.states || [];
  const now = Date.now();
  e.spreadHist.push({ ts: now, val: f.spread });
  while (e.spreadHist.length && e.spreadHist[0].ts < now - 10_000) e.spreadHist.shift();
  e.tally.frames++;
  if (!f.gated) e.tally.open++;
}

// Seed from the REST hydrate blob ({ tick, dp, profile:{idx:[buy,sell,reloads,maxDisp]}, states, ... }).
function seedFromBlob(e: Eng, b: any) {
  if (!b) return;
  e.hasData = true;
  e.tick = b.tick || e.tick; e.prec = b.dp ?? decimalsForTick(e.tick);
  e.mid = b.mid; e.bid = b.bid; e.ask = b.ask; e.spread = b.spread ?? 0;
  e.pace = b.pace ?? 0; e.rvol = b.rvol ?? 0; e.imbalance = b.imbalance ?? 0.5; e.thin = !!b.thin;
  if (b.dvps != null) { e.dvps = b.dvps; e.dvpsSeeded = !!b.dvpsSeeded; }
  if (b.buyScore) e.buyScore = b.buyScore;
  e.gated = !!b.gated;
  e.rows = new Map();
  for (const [idx, v] of Object.entries(b.profile || {})) {
    const [buy, sell, reloads, maxDisp] = v as number[];
    e.rows.set(Number(idx), { buy, sell, reloads, ice: maxDisp > 0 ? Math.round(((buy + sell) / maxDisp) * 100) / 100 : 0 });
  }
  e.states = b.states || [];
  // §D5 tape ring: [ts, price, size, isBuy] chronological-ascending — seed the
  // strip (and the last-trade caret) so the card isn't blank until live prints.
  if (Array.isArray(b.tape) && b.tape.length) {
    e.tape = b.tape.slice(-12).reverse()
      .map((t: any[]) => ({ id: e.tapeId++, px: Number(t[1]), size: Number(t[2]) || 0, side: t[3] ? 'buy' : 'sell' }))
      .filter((t: any) => Number.isFinite(t.px));
    if (e.tape.length) e.lastPx = e.tape[0].px;
  }
}

// Seed the E5 tally from the REST signals log (structural fires from TODAY only —
// the log itself is capped, not day-scoped).
function seedTally(e: Eng, signals: any[]) {
  if (!Array.isArray(signals)) return;
  const dayStart = new Date(); dayStart.setHours(0, 0, 0, 0);
  for (const sig of signals) {
    const ts = typeof sig.ts === 'number' ? sig.ts : Date.parse(sig.ts);
    if (!Number.isFinite(ts) || ts < dayStart.getTime()) continue;
    tallySignal(e, sig.state, sig.cls);
  }
}

function classifySide(e: Eng, px: number): 'buy' | 'sell' {
  if (e.ask > 0 && px >= e.ask) return 'buy';
  if (e.bid > 0 && px <= e.bid) return 'sell';
  return px >= e.mid ? 'buy' : 'sell';
}

/* E3 (approx): verdict clock. A live FLIP arm counts down a client-side TTL;
   FLIP_FAIL renders the resolution; BREAK_CONFIRM/STOP_RUN render "held".
   Exact arm + TTL comes with the §F `flip` export. */
function buildClock(key: string, active: any, e: Eng, now: number) {
  if (!active || active.px == null) return null;
  const trigPx = active.px, tick = e.tick, prec = e.prec;
  const confirmPx = +(trigPx + 2 * tick).toFixed(prec);
  const failPx = +(trigPx - 2 * tick).toFixed(prec);
  if (key === 'flip') {
    const left = Math.max(0, FLIP_ARM_TTL_S - Math.floor((now - active.since) / 1000));
    return { mode: 'pending', left, ttl: FLIP_ARM_TTL_S, confirmPx, failPx, trigPx, prec };
  }
  if (key === 'flipfail') {
    const bt = (active.metrics && active.metrics.belowTicks) || 2;
    return { mode: 'failed', confirmPx, failPx: +(trigPx - bt * tick).toFixed(prec), trigPx, prec };
  }
  if (key === 'breakconfirm' || key === 'stoprun') return { mode: 'confirmed', left: 0, ttl: 0, confirmPx, failPx, trigPx, prec };
  return null;
}

/* E1: gate checklist off the §F `gates` frame export (null until it ships). */
function buildGate(e: Eng, seqId: string) {
  const g = e.gates;
  if (!g) return null;
  if ((seqId === 'flip' || seqId === 'breakout') && g.break) {
    const b = g.break;
    return { kind: 'break', label: 'Break needs', items: [
      { k: 'Lifts', cur: `${b.lifts}/${b.needLifts}`, ok: b.lifts >= b.needLifts },
      { k: 'Progress', cur: `+${b.ticks}t`, need: `+${b.needTicks}t`, ok: b.ticks >= b.needTicks },
      { k: 'Speed', cur: `${(+b.pace).toFixed(1)}×`, need: `${b.needPace}×`, ok: b.pace >= b.needPace },
    ] };
  }
  if ((seqId === 'supply' || seqId === 'turn') && g.turn) {
    const t = g.turn;
    return { kind: 'turn', label: 'Turn needs', items: [
      { k: 'Sellers', cur: t.sellTrend != null ? `${(+t.sellTrend).toFixed(1)}×` : '—', need: `≤${t.needSell}×`, ok: t.sellTrend != null && t.sellTrend <= t.needSell },
      { k: 'Buyers', cur: t.buyTrend != null ? `${(+t.buyTrend).toFixed(1)}×` : '—', need: `≥${t.needBuy}×`, ok: t.buyTrend != null && t.buyTrend >= t.needBuy },
      { k: 'At level', cur: t.atLevel ? 'yes' : 'no', ok: !!t.atLevel },
      { k: 'Holding', cur: t.holding ? 'yes' : 'no', ok: !!t.holding },
    ] };
  }
  return null;
}

/* E6: rotation dials off the §F `flow` export (null until it ships). */
function buildDials(e: Eng, seqId: string) {
  const g = e.gates;
  if (!g || !g.flow || (seqId !== 'supply' && seqId !== 'turn')) return null;
  const f = g.flow;
  const sellR = f.sellPrior ? f.sellDps / f.sellPrior : null;
  const buyR = f.buyPrior ? f.buyDps / f.buyPrior : null;
  if (sellR == null || buyR == null) return null;
  return {
    sell: { rate: f.sellDps, ratio: sellR, trip: 0.4, ok: sellR <= 0.4 },
    buy: { rate: f.buyDps, ratio: buyR, trip: 1.5, ok: buyR >= 1.5 },
  };
}

function buildSnap(e: Eng, symbol: string): any {
  if (!e.hasData) return { thin: true, symbol, rvol: null };
  if (e.thin) return { thin: true, symbol, rvol: e.rvol };
  const now = Date.now();
  const tick = e.tick, prec = e.prec;
  const center = Math.round(e.mid / tick);
  const lastIdx = e.lastPx ? Math.round(e.lastPx / tick) : center;

  // Pick the dominant verdict: freshest STRUCTURAL state (a badge at a level
  // that matters) while its story is still live; a structural badge older than
  // the horizon must not pin the card over a fresher tactical story. Tactical
  // fills in only when nothing structural qualifies. A recent signal still
  // wins if newer (signals are structural-only unless the server routes
  // tactical too).
  const STRUCT_MAX_AGE_MS = 90_000;
  let active: any = null;
  for (const st of e.states) {
    if (st.cls !== 'tactical' && now - st.since <= STRUCT_MAX_AGE_MS && (!active || st.since > active.since)) active = st;
  }
  if (!active) for (const st of e.states) if (!active || st.since > active.since) active = st;
  if (e.signal && now - e.signal.ts < 10_000 && (!active || e.signal.ts >= active.since)) {
    active = { state: e.signal.state, px: e.signal.px, since: e.signal.ts, metrics: e.signal.metrics };
  }
  const key = active ? (STATE_MAP[active.state] || 'watching') : 'watching';
  const meta = L_STATES[key];
  const trigPx = active ? active.px : null;
  const trigStr = trigPx != null ? '$' + trigPx.toFixed(prec) : '$' + e.mid.toFixed(prec);
  const centerStr = '$' + e.mid.toFixed(prec);
  let trigIdx = trigPx != null ? Math.round(trigPx / tick) : null;

  // E4: freshest state badge per price row — history that ages out visually,
  // instead of paint that reads as a live arm (the stale FLIP@2.29 problem).
  const badgeByIdx = new Map<number, any>();
  for (const st of e.states) {
    const idx = Math.round(st.px / tick);
    const cur = badgeByIdx.get(idx);
    if (!cur || st.since > cur.since) badgeByIdx.set(idx, st);
  }

  // Model hold-call badge per price row: the vision frame's levels arrive with
  // the model's P(hold) already joined server-side (frame.ts decorate). Badge
  // only the calls that graded on labeller ground truth: good families, hold
  // direction (p ≥ 0.5). Support-holds read bullish, resistance-holds bearish.
  // Several families can round into one row — highest conviction wins. Frames
  // are 1/min; past MODEL_STALE_MS the chip dims to history, like E4 badges.
  const modelByIdx = new Map<number, any>();
  const vf = e.vision;
  if (vf && vf.warm !== false && Array.isArray(vf.levels)) {
    // Staleness = the FEED stopped. Only frame.ts is the true emit time —
    // computedTs is BAR-anchored on the live wire (it equals barMs; verified
    // 2026-09-03: computedTs age 104s while ts age 39s on the same frame), so
    // gating on it re-created the always-stale bug the first fix tried to
    // kill. ts is the same field VisionLayer's freshness guard trusts.
    const vStale = now - (vf.ts || 0) > MODEL_STALE_MS;
    for (const lv of vf.levels) {
      if (typeof lv.p !== 'number' || !MODEL_FAMS.has(lv.family)) continue;
      // Two chip kinds, both calibrated on labeller ground truth: HOLD (p≥.5,
      // toned by side) and PAPER (p≤.35 — the level is weak; strength info,
      // not a direction call). The ambiguous middle stays chipless.
      const isHold = lv.p >= MODEL_P_MIN, isPaper = lv.p <= MODEL_WEAK_MAX;
      if (!isHold && !isPaper) continue;
      const idx = Math.round(lv.price / tick);
      const cur = modelByIdx.get(idx);
      // several families can glue into one row — the more decisive read wins
      if (cur && Math.abs(cur.p - 0.5) >= Math.abs(lv.p - 0.5)) continue;
      // level.type is the raw blob type ("5m_EMA21") — the CONCRETE indicator
      // behind a generic family like ma-5m. Prettify to "EMA21·5m" for display.
      const ind = typeof lv.type === 'string' && lv.type.includes('_')
        ? lv.type.split('_').slice(1).join('_').toUpperCase() + '·' + lv.type.split('_')[0]
        : null;
      modelByIdx.set(idx, {
        p: lv.p, pct: Math.round(lv.p * 100), px: +(idx * tick).toFixed(prec),
        kind: isHold ? 'hold' : 'paper', ind,
        tone: isHold ? (lv.side === 'support' ? 'up' : 'down') : 'paper',
        side: lv.side, family: lv.family, stale: vStale, tape: !!vf.flow,
      });
    }
  }
  const mode = e.uiMode === 'fsm' ? 'fsm' : 'model';
  // Model verdict: the strongest current hold-call runs the card header in
  // model view. tier 'loud' at p≥0.8 — the 88%-on-labeller-ground-truth tier.
  let mvTop: any = null;
  for (const m of modelByIdx.values()) if (m.kind === 'hold' && (!mvTop || m.p > mvTop.p)) mvTop = m;
  const mvFresh = mvTop && !mvTop.stale;
  // Age shown = seconds since the read's bar CLOSED (barMs is the bar start).
  const mvAge = vf ? Math.max(0, Math.floor((now - ((vf.barMs ? vf.barMs + 60_000 : vf.ts) || now)) / 1000)) : 0;
  // Episode narration — the model's structure classification, displayed as
  // narration ONLY: calibrated vs the labeller's episode labels (the nightly
  // card grades it), but its top-decile DIRECTIONAL read measured negative,
  // so it must never carry tone, flash, or a trigger.
  const ep = vf && vf.mv1 && vf.mv1.top;
  const vFeedLive = vf && now - (vf.ts || 0) <= MODEL_STALE_MS;
  const mvNote = ep && vFeedLive
    ? `forming: ${String(ep.class).replace(/_/g, ' ')} @${ep.tf} · ${Math.round((ep.p || 0) * 100)}% — structure read, not direction`
    : null;
  const mvState = mvFresh ? {
    key: mvTop.side === 'support' ? 'mvhold' : 'mvfade',
    label: mvTop.side === 'support' ? 'Support Holds' : 'Resistance Holds',
    tone: mvTop.tone, icon: mvTop.side === 'support' ? 'turn' : 'fade',
    tier: mvTop.p >= 0.8 ? 'loud' : 'quiet', trust: mvTop.pct, kind: 'verdict',
    trigger: mvTop.px, since: mvAge, note: mvNote,
    receipt: `<b>${mvTop.ind || mvTop.family}</b> ${mvTop.side} · model holds <b>${mvTop.pct}%</b> · ` +
      `${mvTop.tape ? 'tape-informed' : 'tape-masked'} · ${modelByIdx.size} level${modelByIdx.size === 1 ? '' : 's'} read`,
  } : {
    key: 'watching', label: 'Watching', tone: 'idle', icon: 'eye', tier: 'idle',
    trust: undefined, kind: undefined, trigger: null, since: mvAge, note: mvNote,
    receipt: !vf ? 'no model frame yet for this symbol'
      : mvTop ? 'model read is stale — awaiting the next bar close'
      : 'model live · no confident holds at nearby levels',
  };

  // In model view the model's strongest call owns the trigger row marker.
  if (mode === 'model') trigIdx = mvFresh ? Math.round(mvTop.px / tick) : null;

  // Book rows — a contiguous 15-row window centered on mid, air pockets included.
  let maxSide = 1, maxTotal = 1;
  const raw: any[] = [];
  for (let i = 0; i < NROWS; i++) {
    const idx = center + (MID - i);
    const px = +(idx * tick).toFixed(prec);
    const c = e.rows.get(idx) || { buy: 0, sell: 0, reloads: 0, ice: 0 };
    maxSide = Math.max(maxSide, c.buy, c.sell); maxTotal = Math.max(maxTotal, c.buy + c.sell);
    raw.push({ idx, px, c });
  }
  const rows = raw.map(({ idx, px, c }) => {
    const b = badgeByIdx.get(idx);
    const bKey = b ? STATE_MAP[b.state] : null;
    return {
      px, buy: c.buy, sell: c.sell,
      buyW: Math.min(100, (c.buy / maxSide) * 100), sellW: Math.min(100, (c.sell / maxSide) * 100),
      buyPct: (c.buy / (c.buy + c.sell || 1)) * 100,
      buyUsd: c.buy * px, sellUsd: c.sell * px,
      air: (c.buy + c.sell) < 0.05 * maxTotal,
      isLast: idx === lastIdx, isTrig: trigIdx != null && idx === trigIdx,
      reload: c.reloads, ice: c.ice,
      // Model view: FSM row badges off — the model's chips ARE the read.
      badge: mode === 'model' ? null : (bKey && LCODE[bKey] ? { code: LCODE[bKey], tone: (L_STATES[bKey] || {}).tone || 'idle', stale: now - b.since > BADGE_STALE_MS } : null),
      model: modelByIdx.get(idx) || null,
    };
  });

  // sequence stepper
  const seqId = SEQ_OF[key]; const seqDef = L_SEQ[seqId]; const stage = STAGE_OF[key];
  const stages = seqDef.stages.map((label: string, i: number) => {
    let status = 'pending';
    if (stage >= 0) { if (i < stage) status = 'done'; else if (i === stage) status = FLASHY.has(key) ? 'fired' : 'active'; }
    return { label, status };
  });

  // spread-tighten tell: current ≤ 50% of the recent max within 10s
  const recentMax = e.spreadHist.reduce((a: number, h: any) => Math.max(a, h.val), 0);
  const tight = e.spreadHist.length > 3 && recentMax > 0 && e.spread <= 0.5 * recentMax;

  const net = clamp((e.imbalance - 0.5) * 2, -1, 1);
  const paceLvl = clamp(Math.round(e.pace), 0, 5);
  const t = e.tally;
  // Buy score (1–100, 50 = balanced) — scored server-side over the rolling 60s,
  // the SAME definition the chart sub-pane plots per bar. Only val+band are on
  // the wire; the label and the frame-over-frame delta are derived here.
  const bs = e.buyScore;
  const buyScore = bs ? {
    val: bs.val, band: bs.band,
    label: BS_LABEL[bs.band] || 'Balanced',
    delta: e.bsPrev == null ? 0 : bs.val - e.bsPrev,
  } : null;
  return {
    thin: false, prec, symbol, mid: e.mid, mode,
    state: mode === 'model' ? mvState
      : { key, label: meta.label, tone: meta.tone, icon: meta.icon, tier: meta.tier, trust: meta.trust, kind: meta.kind,
        trigger: trigPx,
        receipt: buildReceipt(key, active ? active.metrics || {} : {}, trigStr, centerStr, { dvps: e.dvps, trigPx }),
        since: active ? Math.max(0, Math.floor((now - active.since) / 1000)) : 0 },
    feeding: mode !== 'model' && key === 'feeding',
    seq: mode === 'model' ? null : { name: seqDef.name, stages, tone: meta.tone },
    clock: mode === 'model' ? null : buildClock(key, active, e, now),
    gate: buildGate(e, seqId),
    dials: buildDials(e, seqId),
    ruler: e.dvps != null ? { dvps: e.dvps, seeded: e.dvpsSeeded } : null,
    buyScore,
    tally: { ff: t.ff, bc: t.bc, sr: t.sr, turn: t.turn, loud: t.ff + t.bc + t.sr + t.turn,
      gopen: t.frames >= 10 ? Math.round((t.open / t.frames) * 100) : null },
    pace: { level: paceLvl, rvol: (e.rvol || 0).toFixed(1), hot: paceLvl >= 4 },
    imb: { net, buyShare: e.imbalance },
    spread: { val: e.spread, tight },
    rows, tape: e.tape.slice(),
    flashId: e.flashId, flashKind: e.flashKind,
    lastPx: +(lastIdx * tick).toFixed(prec),
  };
}

function useLadderData(symbol: string, interp?: string) {
  const engRef = useRefL<Eng>(freshEng());
  const [snap, setSnap] = useStateL<any>({ thin: true, symbol });

  // Interpretation-layer switch reaches the feed reducers via the eng ref so
  // flash routing and buildSnap agree without re-subscribing anything.
  useEffectL(() => {
    engRef.current.uiMode = interp === 'fsm' ? 'fsm' : 'model';
    setSnap(buildSnap(engRef.current, symbol));
  }, [interp]);

  useEffectL(() => {
    let alive = true;
    const prevMode = engRef.current.uiMode;
    engRef.current = freshEng();
    engRef.current.uiMode = prevMode;
    setSnap({ thin: true, symbol });

    // REST hydrate → seed the profile + today's signal tally before live frames arrive.
    getLadder(symbol).then((resp: any) => {
      if (!alive || !resp) return;
      if (resp.active && resp.snapshot) seedFromBlob(engRef.current, resp.snapshot);
      seedTally(engRef.current, resp.signals);
    }).catch(() => { /* stays thin until a live frame */ });

    const offLad = subscribeLadder(symbol, (f: any) => {
      const e = engRef.current;
      if (f.type === 'ladder-signal') {
        e.signal = { state: f.state, px: f.px, metrics: f.metrics || {}, ts: f.ts || Date.now() };
        tallySignal(e, f.state, f.cls);
        const key = STATE_MAP[f.state];
        // FSM fires only drive the flash/sound when the FSM view is on — in
        // model view the model's own strong calls are the cue.
        if (e.uiMode !== 'model' && FLASHY.has(key)) { e.flashId++; e.flashKind = (key === 'feeding' || key === 'flipfail' || key === 'retreat') ? 'feeding' : 'flip'; }
      } else {
        applyFrame(e, f);
      }
    });

    // Real prints (market:<SYM>) drive the tape + last price; side classified vs the quote.
    const offTr = subscribeTrades(symbol, (t: any) => {
      const e = engRef.current; const px = Number(t.price); const size = Number(t.size) || 0;
      if (!px) return;
      e.lastPx = px;
      e.tape.unshift({ id: e.tapeId++, px, size, side: classifySide(e, px) });
      if (e.tape.length > 12) e.tape.length = 12;
    });

    // Decouple render rate from feed rate — repaint ~4fps off the accumulated refs.
    const id = setInterval(() => { if (alive) setSnap(buildSnap(engRef.current, symbol)); }, 250);
    return () => { alive = false; clearInterval(id); offLad(); offTr(); };
  }, [symbol]);

  // Model reads (vision frames) drive the hold-call row badges. Wholesale
  // frame replace with the VisionLayer seq/ts guard (seq resets on respawn).
  // Its own effect, keyed on symbol AND lane (lib/vision-lane.ts; MV-5 only
  // since MV-4 was retired), so a lane switch re-keys only this feed — the ladder,
  // tape and tally above keep running. Declared after the effect that swaps in
  // a fresh engine on a symbol change, so on a symbol change it writes into the
  // new one.
  const lane = useVisionLane();
  useEffectL(() => {
    let alive = true;
    // Guard reset: the lanes' publishers count seq apart — the old lane's
    // frame left in place would drop the new lane's frames for up to a bar,
    // and its badges would linger. mvStrongKeys is kept, so the cue below
    // fires only for a strong call the card was not already showing.
    engRef.current.vision = null;
    getVision(symbol, lane).then((r: any) => {
      if (!alive || !r || !r.active || !r.frame) return;
      const e = engRef.current;
      if (!e.vision || r.frame.seq >= e.vision.seq) e.vision = r.frame;
    }).catch(() => {});
    const offVis = subscribeVision(symbol, (fr: any) => {
      if (!alive || !fr) return;
      const e = engRef.current;
      const prev = e.vision;
      if (prev && fr.seq < prev.seq && fr.ts <= prev.ts + 5000) return;
      e.vision = fr;
      // Model-native cue: flash/sound once when a NEW strong hold-call (p≥0.8,
      // the 88%-on-ground-truth tier) appears — the model view's answer to the
      // FSM's loud fires. Keyed per level so a persisting call fires once.
      if (fr.warm !== false && Array.isArray(fr.levels)) {
        const strong = new Set<string>();
        for (const lv of fr.levels) {
          if (typeof lv.p !== 'number' || lv.p < 0.8 || !MODEL_FAMS.has(lv.family)) continue;
          strong.add(lv.side + '|' + lv.price.toFixed(2));
        }
        if (e.uiMode === 'model') {
          for (const k of strong) if (!e.mvStrongKeys.has(k)) {
            e.flashId++; e.flashKind = k.startsWith('support') ? 'flip' : 'feeding';
            break;
          }
        }
        e.mvStrongKeys = strong;
      }
    }, lane);
    return () => { alive = false; offVis(); };
  }, [symbol, lane]);

  return snap;
}

// Shared flash/sound cue: fires once per new loud signal on the snapshot.
function useLadderFlash(s: any, sound: boolean) {
  const [flashOn, setFlashOn] = useStateL(false);
  const lastFlash = useRefL(0);
  useEffectL(() => {
    if (!s || s.thin) return;
    if (s.flashId === lastFlash.current) return;
    lastFlash.current = s.flashId;
    if (s.flashId === 0) return; // nothing has fired yet
    setFlashOn(true);
    const t = setTimeout(() => setFlashOn(false), 720);
    if (sound) blip(s.flashKind === 'feeding' ? 'feeding' : 'flip');
    return () => clearTimeout(t);
  }, [s && s.flashId]);
  return flashOn;
}

/* ============================================================
   SUB-VIEWS (render the snapshot `s`)
   ============================================================ */
function TrustChip({ kind, trust }: any) {
  if (trust == null) return null;
  const label = kind === 'verdict' ? 'verdict' : kind === 'pending' ? 'pending' : 'narration';
  return hl('span', { className: 'lv-trust ' + (kind === 'verdict' ? 'hi' : kind === 'pending' ? 'mid' : 'lo') },
    hl('b', null, label), trust + '%');
}
function TierBadge({ tier }: any) {
  if (!tier || tier === 'idle') return null;
  return hl('span', { className: 'lv-tier ' + tier }, tier === 'loud' ? 'LOUD' : 'quiet');
}

function Verdict({ s, flashOn, backside }: any) {
  const st = s.state; const tc = 't-' + st.tone;
  return hl('div', { className: 'lad-verdict ' + tc + (flashOn ? ' flash' : '') },
    hl('div', { className: 'lv-top' },
      hl('span', { className: 'lv-label' }, 'When'),
      hl(TierBadge, { tier: st.tier }),
      hl(TrustChip, { kind: st.kind, trust: st.trust }),
      hl('span', { className: 'lv-since mono' }, st.since + 's')),
    hl('div', { className: 'lv-main' },
      hl('span', { className: 'lv-ic' }, LIcon[st.icon]()),
      hl('span', { className: 'lv-state' }, st.label),
      st.trigger != null ? hl('span', { className: 'lv-trigger' },
        hl('span', { className: 'lvt-px num' }, '$' + st.trigger.toFixed(s.prec)),
        hl('span', { className: 'lvt-k' }, st.kind === 'verdict' ? 'level' : 'trigger')) : null),
    hl('div', { className: 'lv-receipt', dangerouslySetInnerHTML: { __html: st.receipt } }),
    st.note ? hl('div', { className: 'lv-note mv-note', title: 'Structure classification (graded nightly vs the labeller). Its directional read measured negative — narration only, never a cue.' }, st.note) : null,
    backside ? hl('div', { className: 'lv-note' }, 'Below session VWAP — most flips fail on the backside. ', hl('b', null, 'The fail is the signal (73%).')) : null);
}

/* ---- verdict clock (E3): pending arm counts down; confirmed/failed resolve ---- */
function VerdictClock({ c }: any) {
  const px = (v: number) => '$' + v.toFixed(c.prec);
  if (c.mode === 'failed') {
    return hl('div', { className: 'lad-clock failed' },
      hl('span', { className: 'clk-ic' }, LIcon.boltx()),
      hl('span', { className: 'clk-txt' }, 'Reclaim died — printed under ', hl('b', null, px(c.failPx))));
  }
  const pct = c.ttl ? Math.max(0, Math.min(1, c.left / c.ttl)) : 1;
  const label = c.mode === 'confirmed' ? 'Confirmed' : 'Reclaim attempt';
  return hl('div', { className: 'lad-clock ' + c.mode },
    hl('div', { className: 'clk-head' },
      hl('span', { className: 'clk-title' }, label, ' ', hl('span', { className: 'num' }, px(c.trigPx))),
      c.mode === 'pending' ? hl('span', { className: 'clk-left num' }, c.left + 's left') : hl('span', { className: 'clk-left ok' }, 'held')),
    c.mode === 'pending' ? hl('div', { className: 'clk-bar' }, hl('i', { style: { width: (pct * 100) + '%' } })) : null,
    hl('div', { className: 'clk-rule' },
      hl('span', { className: 'clk-up' }, 'confirms >', hl('b', null, px(c.confirmPx))),
      hl('span', { className: 'clk-dn' }, 'fails <', hl('b', null, px(c.failPx)))));
}

/* ---- gate checklist (E1): what completes the next loud step, live (§F) ---- */
function GateChecklist({ gate }: any) {
  return hl('div', { className: 'lad-gatechk' },
    hl('div', { className: 'gk-head' }, gate.label),
    hl('div', { className: 'gk-items' },
      gate.items.map((it: any, i: number) => hl('div', { key: i, className: 'gk-item ' + (it.ok ? 'ok' : 'no') },
        hl('span', { className: 'gk-ic' }, it.ok ? LIcon.check() : LIcon.cross()),
        hl('span', { className: 'gk-k' }, it.k),
        hl('span', { className: 'gk-v num' }, it.cur, it.need ? hl('i', null, ' / ' + it.need) : null)))));
}

/* ---- rotation dials (E6): per-side $ flow vs prior-5m, approaching trip points (§F) ---- */
function RotationDials({ dials }: any) {
  const dial = (d: any, side: string) => {
    const frac = Math.max(0.04, Math.min(1, d.ratio / 2.5));
    const tripFrac = Math.min(1, d.trip / 2.5);
    return hl('div', { className: 'rd-dial ' + side + (d.ok ? ' ok' : '') },
      hl('div', { className: 'rd-top' },
        hl('span', { className: 'rd-k' }, side === 'sell' ? 'Sell flow' : 'Buy flow'),
        hl('span', { className: 'rd-need mono' }, side === 'sell' ? 'need ≤0.4×' : 'need ≥1.5×')),
      hl('div', { className: 'rd-track' },
        hl('i', { className: 'rd-fill', style: { width: (frac * 100) + '%' } }),
        hl('span', { className: 'rd-trip', style: { left: (tripFrac * 100) + '%' } })),
      hl('div', { className: 'rd-bot' },
        hl('span', { className: 'rd-ratio num' }, d.ratio.toFixed(1) + '×'),
        hl('span', { className: 'rd-rate mono' }, lDps(d.rate))));
  };
  return hl('div', { className: 'lad-dials' }, dial(dials.sell, 'sell'), dial(dials.buy, 'buy'));
}

/* ---- BUY SCORE gauge (1–100) — same scale, same server definition as the
       chart's buy-score sub-pane, so the gauge and the bars can never disagree.
       50 is the midline (balanced); >50 means buyers paid up. ---- */
function BuyScore({ b, compact }: any) {
  const ticks = [30, 50, 70].map((t: number) => hl('span', { key: t, className: 'bs-tick' + (t === 50 ? ' mid' : ''), style: { left: t + '%' } }));
  if (compact) {
    return hl('div', { className: 'rb-bscore bs-' + b.band, title: 'Buy score ' + b.val + '/100 · ' + b.label },
      hl('span', { className: 'bs-k' }, 'BS'),
      hl('span', { className: 'bs-n num' }, b.val),
      hl('div', { className: 'bs-track sm' }, ticks, hl('i', { className: 'bs-fill', style: { width: b.val + '%' } })));
  }
  return hl('div', { className: 'lad-bscore bs-' + b.band },
    hl('div', { className: 'bs-top' },
      hl('span', { className: 'bs-k' }, 'Buy score'),
      hl('span', { className: 'bs-lbl' }, b.label),
      hl('span', { className: 'bs-delta num' + (b.delta > 0 ? ' up' : b.delta < 0 ? ' down' : '') }, (b.delta > 0 ? '+' : b.delta < 0 ? '−' : '±') + Math.abs(b.delta)),
      hl('span', { className: 'bs-n num' }, b.val, hl('i', null, '/100'))),
    hl('div', { className: 'bs-track' }, ticks, hl('i', { className: 'bs-fill', style: { width: b.val + '%' } })));
}

/* ---- ruler (E2): the $/s baseline every ratio is measured against ---- */
function RulerLine({ ruler }: any) {
  return hl('div', { className: 'lad-ruler' },
    hl('span', { className: 'ru-k' }, 'norm'),
    hl('span', { className: 'ru-v num' }, lDps(ruler.dvps)),
    hl('span', { className: 'ru-tag ' + (ruler.seeded ? 'seeded' : 'floor') }, ruler.seeded ? 'seeded' : 'floor'));
}

/* ---- session tally (E5): standing health check ---- */
function Tally({ t }: any) {
  const part = (n: number, lbl: string) => hl('span', { className: 'tl-part' + (n ? '' : ' zero'), key: lbl }, hl('b', null, n), lbl);
  return hl('div', { className: 'lad-tally' },
    hl('span', { className: 'tl-lead' }, 'Loud today'),
    part(t.ff, 'FF'),
    part(t.bc, 'BC'),
    part(t.sr, 'SR'),
    part(t.turn, 'TURN'),
    t.gopen != null ? hl('span', { className: 'tl-gate' }, 'gate open ', hl('b', null, t.gopen + '%')) : null);
}

function Veto() {
  return hl('div', { className: 'lad-veto' }, LIcon.ban(),
    hl('span', null, hl('b', null, 'Feeding'), ' — hidden seller / possible ATM. Longs vetoed until it clears.'));
}

function Stepper({ seq }: any) {
  const nodes: any[] = [];
  seq.stages.forEach((stg: any, i: number) => {
    if (i > 0) nodes.push(hl('div', { key: 'l' + i, className: 'seq-line' + (seq.stages[i - 1].status === 'done' ? ' done' : '') }));
    const status = stg.status;
    nodes.push(hl('div', { key: 'n' + i, className: 'seq-node ' + status },
      hl('span', { className: 'seq-dot' }, status === 'done' ? svg([hl('path', { key: 'c', d: 'M5 12l4 4 10-10', strokeWidth: 2.6 })]) : status === 'fired' ? LIcon.bolt() : hl('span', { style: { width: 5, height: 5, borderRadius: 5, background: 'currentColor' } })),
      hl('span', { className: 'seq-lbl' }, stg.label)));
  });
  return hl('div', { className: 'lad-seq' },
    hl('div', { className: 'seq-head' },
      hl('span', { className: 'seq-name' }, seq.name),
      hl('span', { className: 'seq-flag mono' }, 'sequence')),
    hl('div', { className: 'seq-track', style: { '--vc': toneVar(seq.tone) } }, nodes));
}

function Telemetry({ s }: any) {
  const pace = s.pace, imb = s.imb, sp = s.spread;
  const bars: any[] = [];
  for (let i = 0; i < 5; i++) bars.push(hl('i', { key: i, className: (i < pace.level ? (pace.hot ? 'hot' : 'on') : ''), style: { height: (40 + i * 15) + '%' } }));
  const netPct = Math.round(imb.net * 100);
  const buyLed = imb.net >= 0;
  return hl('div', { className: 'lad-tele' },
    hl('div', { className: 'tele-cell' },
      hl('span', { className: 'tele-k' }, 'Pace'),
      hl('div', { className: 'pace-bars' }, bars),
      hl('div', { className: 'tele-v' }, pace.rvol, hl('i', null, '× rvol'))),
    hl('div', { className: 'tele-cell' },
      hl('span', { className: 'tele-k' }, 'Imbalance 2m'),
      hl('div', { className: 'imb-wrap' },
        hl('span', { className: 'imb-mid' }),
        hl('span', { className: 'imb-fill ' + (buyLed ? 'buy' : 'sell'), style: { width: Math.abs(imb.net) * 50 + '%' } })),
      hl('div', { className: 'tele-v ' + (buyLed ? 'up' : 'down') }, (buyLed ? '+' : '−') + Math.abs(netPct) + '%')),
    hl('div', { className: 'tele-cell' },
      hl('span', { className: 'tele-k' }, 'Spread'),
      hl('div', { style: { height: 13 } }),
      hl('div', { className: 'tele-v spread-v' + (sp.tight ? ' tight' : '') }, '$' + sp.val.toFixed(2))));
}

function BookRow({ r, prec, tone, trigIcon, hist }: any) {
  const cls = ['lad-row'];
  if (r.air) cls.push('air');
  if (r.isTrig) { cls.push('trig'); cls.push('t-' + tone); }
  else if (r.isLast) cls.push('last');
  const badges: any[] = [];
  // Only badge a genuine iceberg (traded ≫ ever-displayed). The server sends the
  // raw ratio on every row; sub-threshold values are normal, not hidden sellers.
  if (r.ice >= ICE_BADGE_MIN) badges.push(hl('span', { key: 'ice', className: 'lr-badge ice' }, 'ICE ' + Math.round(r.ice) + '×'));
  if (r.reload) badges.push(hl('span', { key: 'rl', className: 'lr-badge reload' }, '↻' + r.reload));
  // E4: state badge — history at this price, dimmed once stale (never a live arm claim)
  if (r.badge && !r.isTrig) badges.push(hl('span', { key: 'st', className: 'lr-badge state t-' + r.badge.tone + (r.badge.stale ? ' stale' : '') }, r.badge.code));
  // Model hold-call chip: the MV read on THIS level. In model view it is the
  // only interpretive chip (FSM badges suppressed); in FSM view it renders
  // beside the badge so the two layers stay comparable on screen.
  if (r.model) badges.push(hl('span', {
    key: 'mv',
    className: 'lr-badge model t-' + r.model.tone + (r.model.stale ? ' stale' : ''),
    title: r.model.kind === 'paper'
      ? `model: ${r.model.ind || r.model.family} ${r.model.side} holds only ${r.model.pct}% — paper level, expect give-way (strength read, not a direction call)` + (r.model.tape ? ' · tape-informed' : ' · tape-masked')
      : `model: ${r.model.ind || r.model.family} ${r.model.side} holds ${r.model.pct}%` + (r.model.tape ? ' · tape-informed' : ' · tape-masked'),
  }, 'AI ' + r.model.pct));
  const marker = r.isTrig ? hl('span', { className: 'lr-trig-ic' }, LIcon[trigIcon || 'bolt']())
    : (r.isLast ? hl('span', { className: 'lr-caret' }, '►') : null);

  if (hist === 'stacked') {
    const tot = r.buy + r.sell || 1;
    const wTot = Math.min(100, ((r.buy + r.sell) / (r._max || 1)) * 100);
    const op = r.air ? 0.34 : 0.85;
    return hl('div', { className: cls.join(' '), style: { gridTemplateColumns: '56px 1fr' } },
      marker,
      hl('span', { className: 'lr-px num' }, r.px.toFixed(prec)),
      hl('div', { style: { height: 11, borderRadius: 3, background: 'var(--surface-3)', overflow: 'hidden' } },
        hl('div', { style: { display: 'flex', width: wTot + '%', height: '100%' } },
          hl('i', { style: { width: (r.sell / tot) * 100 + '%', background: 'var(--down)', opacity: op } }),
          hl('i', { style: { width: (r.buy / tot) * 100 + '%', background: 'var(--up)', opacity: op } }))),
      badges);
  }
  // spine (default): sell diverges left, buy diverges right. Hover = dollar read (E2).
  return hl('div', { className: cls.join(' '), title: lUsd(r.sellUsd) + ' sold · ' + lUsd(r.buyUsd) + ' bought' },
    marker,
    hl('div', { className: 'lr-sell' }, hl('i', { style: { width: r.sellW + '%' } })),
    hl('span', { className: 'lr-px num' }, r.px.toFixed(prec)),
    hl('div', { className: 'lr-buy' }, hl('i', { style: { width: r.buyW + '%' } })),
    badges);
}

function Book({ s, hist }: any) {
  let maxTotal = 1; s.rows.forEach((r: any) => { maxTotal = Math.max(maxTotal, r.buy + r.sell); });
  return hl('div', null,
    hl('div', { className: 'lad-book-h' },
      hl('span', { className: 'lbh-l' }, 'sell'),
      hl('span', { className: 'lbh-c' }, 'price'),
      hl('span', { className: 'lbh-r' }, 'buy')),
    hl('div', { className: 'lad-book' },
      s.rows.map((r: any) => hl(BookRow, { key: r.px, r: Object.assign({}, r, { _max: maxTotal }), prec: s.prec, tone: s.state.tone, trigIcon: s.state.icon, hist }))));
}

function Tape({ tape }: any) {
  let mx = 1; tape.forEach((t: any) => { mx = Math.max(mx, t.size); });
  return hl('div', { className: 'lad-tape-wrap' },
    hl('div', { className: 'lad-tape' },
      tape.map((t: any) => hl('div', { key: t.id, className: 'tp-print ' + t.side + (t.size > mx * 0.62 ? ' big' : '') },
        hl('span', { className: 'tp-px num' }, t.px.toFixed(2)),
        hl('span', { className: 'tp-sz num' }, '×' + lFmtSz(t.size))))));
}

function Gate({ symbol, rvol }: any) {
  return hl('div', { className: 'lad-gate' },
    hl('div', { className: 'lad-gate-ic' }, LIcon.thin()),
    hl('div', { className: 'lad-gate-t' }, 'TAPE TOO THIN'),
    hl('div', { className: 'lad-gate-s' }, 'Below the tape-surge minimum — not enough prints to classify flow. States are disabled until the tape wakes up.'),
    hl('div', { className: 'lad-gate-meta' },
      hl('b', null, (rvol != null ? Number(rvol).toFixed(1) : '—') + '× rvol'),
      hl('b', null, 'gate: TAPS_MIN')));
}

// E3 honest framing: an in-flight flip below session VWAP is a backside reclaim.
const isBackside = (s: any, vwap: any) => !!(s && !s.thin && s.state.key === 'flip' && vwap != null && vwap > 0 && s.mid < vwap);

/* ============================================================
   CARD
   ============================================================ */
export function LadderCard(props: any) {
  const { symbol, pos, collapsed, onMove, onToggle, onClose, hist, sound, showTape, docked, vwap } = props;
  const cardRef = useRefL<any>(null);
  // Interpretation layer: model by default (the study's verdict); FSM kept as
  // a fallback view. Sticky per browser.
  const [interp, setInterp] = useStateL<string>(() => {
    try { return localStorage.getItem('legend.ladder.interp') || 'model'; } catch { return 'model'; }
  });
  const flipInterp = () => setInterp((v: string) => {
    const nv = v === 'model' ? 'fsm' : 'model';
    try { localStorage.setItem('legend.ladder.interp', nv); } catch { /* private mode */ }
    return nv;
  });
  const s = useLadderData(symbol, interp);
  const flashOn = useLadderFlash(s, !!sound);

  function startDrag(e: any) {
    if (e.target.closest('button') || e.target.closest('a')) return;
    e.preventDefault();
    const ox = (pos.x == null && cardRef.current) ? cardRef.current.offsetLeft : (pos.x || 0);
    const sx = e.clientX, sy = e.clientY, oy = pos.y;
    const move = (ev: any) => onMove({ x: ox + (ev.clientX - sx), y: oy + (ev.clientY - sy) });
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
  }

  const thin = !s || s.thin;
  const head = hl('div', { className: 'lad-head', onPointerDown: docked ? undefined : startDrag },
    hl('span', { className: 'lad-ic' }, LIcon.ladder()),
    hl('span', { className: 'pc-sym' }, symbol),
    hl('span', { className: 'lad-kicker' }, 'Ladder'),
    hl('span', { className: 'lad-live-dot' + (thin ? ' thin' : '') }),
    hl('div', { className: 'pc-actions' },
      hl('button', {
        className: 'lad-interp-btn' + (interp === 'model' ? ' mv' : ''), onClick: flipInterp,
        title: interp === 'model' ? 'Model view (P(hold) reads) — click for the FSM badges' : 'FSM view (legacy badges) — click for the model reads',
      }, interp === 'model' ? 'MV' : 'FSM'),
      hl('button', { className: 'icon-btn sm', onClick: onToggle, title: collapsed ? 'Expand' : 'Collapse' }, Icon.chev()),
      hl('button', { className: 'icon-btn sm', onClick: onClose, title: 'Close card' }, Icon.x())));

  const cardStyle = docked ? undefined : (pos.x == null ? { right: '14px', top: pos.y + 'px' } : { left: pos.x + 'px', top: pos.y + 'px' });
  return hl('div', { className: 'lad-card' + (collapsed ? ' collapsed' : '') + (docked ? ' docked' : ''), style: cardStyle, ref: cardRef },
    head,
    hl('div', { className: 'lad-body' },
      thin ? hl(Gate, { symbol, rvol: s && s.rvol })
        : [
          hl(Verdict, { key: 'v', s, flashOn, backside: isBackside(s, vwap) }),
          s.clock ? hl(VerdictClock, { key: 'clk', c: s.clock }) : null,
          s.feeding ? hl(Veto, { key: 'veto' }) : null,
          s.gate ? hl(GateChecklist, { key: 'gate', gate: s.gate }) : null,
          s.dials ? hl(RotationDials, { key: 'dials', dials: s.dials }) : null,
          hl('div', { key: 'tele', className: 'lad-metrics' },
            s.buyScore ? hl(BuyScore, { b: s.buyScore }) : null,
            s.ruler ? hl(RulerLine, { ruler: s.ruler }) : null,
            hl(Telemetry, { s })),
          s.seq ? hl(Stepper, { key: 'seq', seq: s.seq }) : null,
          hl(Book, { key: 'book', s, hist: hist || 'spine' }),
          s.mode === 'model' ? null : hl(Tally, { key: 'tally', t: s.tally }),
          showTape !== false ? hl(Tape, { key: 'tape', tape: s.tape }) : null,
          hl('div', { key: 'foot', className: 'lad-foot' },
            hl('span', { className: 'lf-dot' }),
            hl('span', null, 'traded volume · top-of-book heuristics · not an L2 book')),
        ]));
}

/* ============================================================
   EMBEDDED RIBBON — the "when-machine" as a chart-top band
   Verdict + inline sequence + telemetry in one horizontal strip
   welded to the top edge of the chart plane. No book/tape here —
   the chart itself is the price surface.
   ============================================================ */
function RibbonSeq({ seq }: any) {
  const nodes: any[] = [];
  seq.stages.forEach((stg: any, i: number) => {
    if (i > 0) nodes.push(hl('span', { key: 'l' + i, className: 'rbs-line' + (seq.stages[i - 1].status === 'done' ? ' done' : '') }));
    const status = stg.status;
    nodes.push(hl('span', { key: 'n' + i, className: 'rbs-node ' + status },
      hl('span', { className: 'rbs-dot' }, status === 'done' ? svg([hl('path', { key: 'c', d: 'M5 12l4 4 10-10', strokeWidth: 3 })]) : status === 'fired' ? LIcon.bolt() : null),
      hl('span', { className: 'rbs-lbl' }, stg.label)));
  });
  return hl('div', { className: 'rb-seq', style: { '--vc': toneVar(seq.tone) } },
    hl('span', { className: 'rb-seq-name' }, seq.name),
    hl('div', { className: 'rb-seq-track' }, nodes));
}

/* CURRENTLY UNMOUNTED. The chart-top ribbon band was removed from both the main
   terminal and detached windows — it held 66px of chart height permanently to
   surface what the full LadderCard shows better on demand, and its only unique
   affordance (the expand button) now lives in the header icon row. Kept, with its
   dock.css rules, so restoring the band is a one-line remount rather than a
   rewrite; delete both if the icon-row layout sticks. */
export function LadderRibbon(props: any) {
  const { symbol, sound, onExpand, expanded } = props;
  const s = useLadderData(symbol);
  const flashOn = useLadderFlash(s, !!sound);

  if (!s || s.thin) {
    return hl('div', { className: 'chart-ribbon thin' },
      hl('span', { className: 'rb-ic thin' }, LIcon.thin()),
      hl('div', { className: 'rb-verdict-txt' },
        hl('div', { className: 'rb-state' }, 'Tape too thin', hl('span', { className: 'rb-since mono' }, (s && s.rvol != null ? Number(s.rvol).toFixed(1) : '—') + '×')),
        hl('div', { className: 'rb-receipt' }, 'Below the tape-surge minimum — flow classification disabled until prints pick up.')),
      onExpand ? hl('button', { className: 'rb-expand' + (expanded ? ' on' : ''), onClick: onExpand, title: expanded ? 'Hide full ladder' : 'Open full ladder widget' },
        hl('span', { className: 'lad-ic' }, LIcon.ladder())) : null);
  }

  const st = s.state, tc = 't-' + st.tone;
  return hl('div', { className: 'chart-ribbon ' + tc + (flashOn ? ' flash' : ''), style: { '--tone': toneVar(st.tone) } },
    hl('div', { className: 'rb-verdict' },
      hl('span', { className: 'rb-ic' }, LIcon[st.icon]()),
      hl('div', { className: 'rb-verdict-txt' },
        hl('div', { className: 'rb-state' }, 'When',
          hl('b', null, st.label),
          hl(TierBadge, { tier: st.tier }),
          hl(TrustChip, { kind: st.kind, trust: st.trust }),
          hl('span', { className: 'rb-since mono' }, st.since + 's')),
        hl('div', { className: 'rb-receipt', dangerouslySetInnerHTML: { __html: st.receipt } })),
      st.trigger != null ? hl('div', { className: 'rb-trigger' },
        hl('span', { className: 'rbt-px num' }, '$' + st.trigger.toFixed(s.prec)),
        hl('span', { className: 'rbt-k' }, st.kind === 'verdict' ? 'level' : 'trigger')) : null),
    hl('div', { className: 'rb-mid' },
      hl('div', { className: 'rb-div' }),
      s.feeding ? hl('div', { className: 'rb-veto' }, LIcon.ban(), hl('span', null, hl('b', null, 'Feeding'), ' · longs vetoed'))
        : s.clock && s.clock.mode === 'pending' ? hl('div', { className: 'rb-clock' },
            hl('span', { className: 'rbc-left num' }, s.clock.left + 's'),
            hl('span', { className: 'rbc-rule mono' }, '>' + s.clock.confirmPx.toFixed(s.prec) + ' / <' + s.clock.failPx.toFixed(s.prec)))
          : s.seq ? hl(RibbonSeq, { seq: s.seq }) : null),
    hl('div', { className: 'rb-tele' },
      hl('div', { className: 'rb-div' }),
      s.buyScore ? hl(BuyScore, { b: s.buyScore, compact: true }) : null,
      s.ruler ? hl('div', { className: 'rb-ruler' },
        hl('span', { className: 'ru-k' }, 'norm'),
        hl('span', { className: 'ru-v num' }, lDps(s.ruler.dvps)),
        !s.ruler.seeded ? hl('span', { className: 'ru-tag floor' }, 'floor') : null) : null,
      hl(Telemetry, { s })),
    onExpand ? hl('button', { className: 'rb-expand' + (expanded ? ' on' : ''), onClick: onExpand, title: expanded ? 'Hide full ladder' : 'Open full ladder widget' },
      hl('span', { className: 'lad-ic' }, LIcon.ladder())) : null);
}
